import React, { useEffect, useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Pencil, Trash2, Search, Users, MapPin, Download, Upload, Eye,
  TreePine, AlertTriangle, FileText, Wallet, Phone, Mail, Shield, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";
import { tokenStore, API_BASE } from "@/lib/api";
import { fileToBase64, errorMsg } from "@/lib/utils-app";

const ID_TYPES = [
  { v: "aadhar", label: "Aadhar Card" },
  { v: "voter_id", label: "Voter ID" },
  { v: "bank_passbook", label: "Bank Passbook" },
  { v: "ration_card", label: "Ration Card" },
  { v: "pan_card", label: "PAN Card" },
  { v: "other", label: "Other (specify)" },
];

const empty = {
  username: "", full_name: "", email: "", phone: "",
  password: "1234", park_ids: [], active: true,
  id_card_number: "",
  id_card_type: "aadhar", id_card_other_label: "",
  parent_name: "", parent_phone: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  bank_account_number: "", bank_name: "", bank_branch: "", bank_ifsc: "",
  id_card_attachment_b64: "", bank_passbook_attachment_b64: "",
};

export default function SupervisorsPage() {
  const [users, setUsers] = useState([]);
  const [parks, setParks] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const [viewing, setViewing] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const viewUser = viewing;

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkExcel, setBulkExcel] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/users", { params: { role: "pmc_supervisor" } }),
      api.get("/parks"),
    ])
      .then(([u, p]) => {
        setUsers(u.data);
        setParks(p.data);
      })
      .catch(() => { toast.error("Failed to load"); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Download excel template
  const downloadTemplate = async () => {
    try {
      const tk = tokenStore.get();
      const res = await fetch(`${API_BASE}/supervisors/template`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return toast.error("Template download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "pmc_supervisors_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel template downloaded");
    } catch { toast.error("Template download failed"); }
  };

  const submitBulk = async () => {
    if (!bulkExcel) return toast.error("Excel file required");
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("excel", bulkExcel);
      const { data } = await api.post("/supervisors/bulk_import", fd);
      setBulkResult(data);
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      load();
    } catch (e) { toast.error(errorMsg(e, "Bulk import failed")); }
    finally { setBulkBusy(false); }
  };

  const parksMap = useMemo(() => Object.fromEntries(parks.map((p) => [p.id, p])), [parks]);

  // KPI: parks coverage
  const activeParks = useMemo(() => parks.filter((p) => p.active !== false), [parks]);
  const orphanParks = useMemo(() => {
    const assigned = new Set();
    users.filter((u) => u.active).forEach((u) => (u.park_ids || []).forEach((pid) => assigned.add(pid)));
    return activeParks.filter((p) => !assigned.has(p.id));
  }, [users, activeParks]);

  // Map: park_id → array of OTHER active PMC supervisors that have it on
  // their list (excludes the one currently being edited). Used to badge
  // shared parks inside the create/edit dialog without blocking selection.
  const sharedWithByParkId = useMemo(() => {
    const m = {};
    users
      .filter((u) => u.active && (!editing || u.id !== editing.id))
      .forEach((u) => {
        (u.park_ids || []).forEach((pid) => {
          if (!m[pid]) m[pid] = [];
          m[pid].push({ id: u.id, username: u.username, full_name: u.full_name });
        });
      });
    return m;
  }, [users, editing]);

  // All active parks are now selectable — multiple PMC supervisors may share
  // the same park (admin will be asked to confirm before saving overlap).
  const availableParks = activeParks;
  const orphanedActiveParks = useMemo(() => {
    // Parks with no assigned PMC supervisor at all.
    return activeParks.filter((p) => !(sharedWithByParkId[p.id] || []).length);
  }, [activeParks, sharedWithByParkId]);

  // Confirmation dialog state for park overlap acknowledgement.
  const [overlapConfirm, setOverlapConfirm] = useState(null); // { items: [{park, sups}], onConfirm }

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    const currentPass = u.plain_password || u.username || "";
    setForm({
      username: u.username, full_name: u.full_name,
      email: u.email || "", phone: u.phone || "", password: currentPass,
      park_ids: u.park_ids || [], active: u.active,
      id_card_number: u.id_card_number || u.username || "",
      id_card_type: u.id_card_type || "aadhar",
      id_card_other_label: u.id_card_other_label || "",
      parent_name: u.parent_name || "", parent_phone: u.parent_phone || "",
      emergency_contact_name: u.emergency_contact_name || "", emergency_contact_phone: u.emergency_contact_phone || "",
      bank_account_number: u.bank_account_number || "", bank_name: u.bank_name || "",
      bank_branch: u.bank_branch || "", bank_ifsc: u.bank_ifsc || "",
      id_card_attachment_b64: undefined,
      bank_passbook_attachment_b64: undefined,
      hourly_cadence_minutes: u.hourly_cadence_minutes ?? null,
      allow_gallery_upload: !!u.allow_gallery_upload,
    });
    setOpen(true);
  };
  const openView = (u) => { setViewUser(u); setViewOpen(true); };

  const togglePark = (pid) => {
    const has = form.park_ids.includes(pid);
    if (has) setForm({ ...form, park_ids: form.park_ids.filter((x) => x !== pid) });
    else if (form.park_ids.length < 4) setForm({ ...form, park_ids: [...form.park_ids, pid] });
    else toast.error("PMC supervisor cannot have more than 4 parks");
  };

  const handleFile = async (kind, file) => {
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setForm((f) => ({ ...f, [`${kind}_attachment_b64`]: b64 }));
      toast.success(`${kind === "id_card" ? "ID" : "Passbook"} attached`);
    } catch { toast.error("File read failed"); }
  };

  const downloadAttachment = async (uid, kind, username) => {
    try {
      const tk = tokenStore.get();
      const res = await fetch(`${API_BASE}/users/${uid}/attachment/${kind}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return toast.error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const ext = blob.type.includes("pdf") ? "pdf" : blob.type.includes("png") ? "png" : "jpg";
      a.download = `${username}_${kind}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };

  const submit = async (forceOverlap = false) => {
    const idNum = (form.id_card_number || "").trim();
    if (!editing && !idNum) return toast.error("ID card number is required (used as username)");
    if (!form.full_name) return toast.error("Full name required");
    if (!editing && !form.password) return toast.error("Password required");
    if (!(form.park_ids.length >= 1 && form.park_ids.length <= 4)) return toast.error("Assign 1-4 parks");

    // Check for PMC park overlap with other active supervisors. If any
    // selected park is already assigned to someone else, open a confirm
    // dialog instead of saving — admin must explicitly acknowledge.
    if (!forceOverlap) {
      const items = [];
      for (const pid of form.park_ids) {
        const others = sharedWithByParkId[pid] || [];
        if (others.length) {
          items.push({ park: parksMap[pid], sups: others });
        }
      }
      if (items.length) {
        setOverlapConfirm({ items });
        return;
      }
    }

    setSaving(true);
    try {
      const cleaned = { ...form };
      ["email", "phone", "parent_name", "parent_phone", "emergency_contact_name", "emergency_contact_phone",
       "bank_account_number", "bank_name", "bank_branch", "bank_ifsc",
       "id_card_other_label"].forEach((k) => {
        if (typeof cleaned[k] === "string" && cleaned[k].trim() === "") delete cleaned[k];
      });
      // Don't send empty attachments
      if (!cleaned.id_card_attachment_b64) delete cleaned.id_card_attachment_b64;
      if (!cleaned.bank_passbook_attachment_b64) delete cleaned.bank_passbook_attachment_b64;

      if (forceOverlap) cleaned.allow_park_overlap = true;

      if (editing) {
        if (!cleaned.password) delete cleaned.password;
        delete cleaned.username;
        await api.put(`/users/${editing.id}`, cleaned);
        toast.success("Supervisor updated");
      } else {
        const payload = { ...cleaned, username: idNum, id_card_number: idNum, role: "pmc_supervisor" };
        await api.post("/users", payload);
        toast.success("Supervisor created");
      }
      setOverlapConfirm(null);
      setOpen(false); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Handle our structured PMC overlap response defensively (it should
      // not reach here when admin already confirmed, but keeps UX sane if
      // the user list was stale).
      if (detail && typeof detail === "object" && detail.code === "pmc_park_overlap") {
        const sups = detail.other_supervisor
          ? [{ id: "", username: detail.other_supervisor.username, full_name: detail.other_supervisor.full_name }]
          : [];
        setOverlapConfirm({
          items: (detail.overlap_park_names || []).map((n) => ({ park: { name: n }, sups })),
          serverDetected: true,
        });
        return;
      }
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join("; ")
        : (typeof detail === "string" ? detail : (detail?.message || "Save failed"));
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const exportXlsx = async () => {
    try {
      const tk = tokenStore.get();
      const res = await fetch(`${API_BASE}/users/export.xlsx?role=pmc_supervisor`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return toast.error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pmc_supervisors_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel downloaded");
    } catch { toast.error("Export failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete supervisor '${u.full_name}'?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const q = search.trim().toLowerCase();
  const list = q
    ? users.filter((u) => `${u.full_name} ${u.username} ${u.email||""} ${u.phone||""}`.toLowerCase().includes(q))
    : users;

  return (
    <AppShell title="PMC Supervisors" subtitle="Field personnel assigned 1–4 parks (rotation if 4)" action={
      <div className="flex gap-2">
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="sup-bulk-import-trigger">
              <Upload className="mr-2 h-4 w-4" />Import Excel
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk import PMC supervisors</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-2">
              <Button variant="outline" onClick={downloadTemplate} data-testid="sup-template-download">
                <Download className="mr-2 h-4 w-4" />Download Excel template
              </Button>
              <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2 text-[11px] leading-relaxed text-sky-900" data-testid="sup-bulk-hint">
                <span className="font-semibold">PMC Supervisor setup</span> &mdash;
                Each supervisor can be assigned <strong>1 to 4 park codes</strong> (separated by commas e.g. <code className="px-1 rounded bg-white">01, 02</code>).
                The ID card number is used as the login username.
              </div>
              <FormField label="Excel file (.xlsx) *">
                <Input type="file" accept=".xlsx" onChange={(e)=>setBulkExcel(e.target.files?.[0])} data-testid="sup-bulk-excel-input" />
              </FormField>
              {bulkResult && (
                <div className="rounded-lg border p-3">
                  <div className="text-sm">
                    Imported <b>{bulkResult.imported}</b>
                    {bulkResult.updated > 0 ? ` (inserted: ${bulkResult.inserted}, updated: ${bulkResult.updated})` : ""}
                    , skipped <b>{bulkResult.skipped}</b>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-muted-foreground">{bulkResult.errors.length} errors</summary>
                      <ul className="mt-1 list-disc pl-5">
                        {bulkResult.errors.slice(0, 30).map((er, i) => <li key={i}>{er}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setBulkOpen(false)}>Close</Button>
              <Button onClick={submitBulk} disabled={bulkBusy} data-testid="sup-bulk-submit">
                <Upload className="mr-2 h-4 w-4" />{bulkBusy ? "Importing…" : "Import"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button variant="outline" onClick={exportXlsx} data-testid="sup-export-button"><Download className="mr-2 h-4 w-4" />Export Excel</Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="supervisor-add-button"><Plus className="mr-2 h-4 w-4" />Add supervisor</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit supervisor" : "New supervisor"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <FormField label="ID card number * (used as username)" full>
                <Input
                  disabled={!!editing}
                  value={form.id_card_number}
                  onChange={(e)=>setForm({...form, id_card_number: e.target.value, username: editing ? form.username : e.target.value})}
                  placeholder="e.g. Aadhar / employee ID number"
                  data-testid="sup-idcard-input"
                />
              </FormField>
              <FormField label="Full name *"><Input value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})} data-testid="sup-fullname-input" /></FormField>
              <FormField label="Contact number"><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="sup-phone-input" /></FormField>
              <FormField label="Email" full><Input type="text" name="sup-email-field" autoComplete="off" data-lpignore="true" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} /></FormField>
              <FormField label={editing ? "New password (leave blank to keep)" : "Password *"} full>
                <PasswordInput value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} data-testid="sup-password-input" testIdToggle="sup-password-toggle" />
              </FormField>

              {/* Identity documents */}
              <div className="col-span-2 mt-2 rounded-md border border-sky-200 bg-sky-50/60 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-900 mb-2 flex items-center gap-1"><Shield className="h-3 w-3" /> Identity document</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="ID type">
                    <select
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      value={form.id_card_type}
                      onChange={(e)=>setForm({...form, id_card_type: e.target.value})}
                      data-testid="sup-idtype-select"
                    >
                      {ID_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                  </FormField>
                  {form.id_card_type === "other" && (
                    <FormField label="Specify"><Input value={form.id_card_other_label} onChange={(e)=>setForm({...form, id_card_other_label: e.target.value})} /></FormField>
                  )}
                  <FormField label="Upload ID card (image/PDF)" full>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e)=>handleFile("id_card", e.target.files?.[0])}
                      className="block w-full text-xs file:mr-2 file:rounded-md file:border file:bg-secondary file:px-2 file:py-1 file:text-xs file:font-medium"
                      data-testid="sup-idfile-input"
                    />
                    {(form.id_card_attachment_b64 || (editing && editing.has_id_card_attachment)) && (
                      <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> {form.id_card_attachment_b64 ? "New file ready to upload" : "Existing file on record"}
                      </div>
                    )}
                  </FormField>
                </div>
              </div>

              {/* Bank account */}
              <div className="col-span-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-900 mb-2 flex items-center gap-1"><Wallet className="h-3 w-3" /> Bank account</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Account number"><Input value={form.bank_account_number} onChange={(e)=>setForm({...form, bank_account_number: e.target.value})} data-testid="sup-bank-acct" /></FormField>
                  <FormField label="IFSC"><Input value={form.bank_ifsc} onChange={(e)=>setForm({...form, bank_ifsc: e.target.value})} data-testid="sup-bank-ifsc" /></FormField>
                  <FormField label="Bank name"><Input value={form.bank_name} onChange={(e)=>setForm({...form, bank_name: e.target.value})} data-testid="sup-bank-name" /></FormField>
                  <FormField label="Branch"><Input value={form.bank_branch} onChange={(e)=>setForm({...form, bank_branch: e.target.value})} data-testid="sup-bank-branch" /></FormField>
                  <FormField label="Upload bank passbook (image/PDF)" full>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e)=>handleFile("bank_passbook", e.target.files?.[0])}
                      className="block w-full text-xs file:mr-2 file:rounded-md file:border file:bg-secondary file:px-2 file:py-1 file:text-xs file:font-medium"
                      data-testid="sup-bankfile-input"
                    />
                    {(form.bank_passbook_attachment_b64 || (editing && editing.has_bank_passbook_attachment)) && (
                      <div className="mt-1 text-[11px] text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> {form.bank_passbook_attachment_b64 ? "New file ready to upload" : "Existing file on record"}
                      </div>
                    )}
                  </FormField>
                </div>
              </div>

              {/* Parents */}
              <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50/60 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 mb-2">Parents / next of kin</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Parent / guardian name"><Input value={form.parent_name} onChange={(e)=>setForm({...form,parent_name:e.target.value})} data-testid="sup-parent-name" /></FormField>
                  <FormField label="Parent contact number"><Input value={form.parent_phone} onChange={(e)=>setForm({...form,parent_phone:e.target.value})} data-testid="sup-parent-phone" /></FormField>
                </div>
              </div>

              {/* Emergency contact */}
              <div className="col-span-2 rounded-md border border-rose-300 bg-rose-50/60 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-900 mb-2">Emergency contact</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Name"><Input value={form.emergency_contact_name} onChange={(e)=>setForm({...form,emergency_contact_name:e.target.value})} data-testid="sup-emer-name" /></FormField>
                  <FormField label="Contact number"><Input value={form.emergency_contact_phone} onChange={(e)=>setForm({...form,emergency_contact_phone:e.target.value})} data-testid="sup-emer-phone" /></FormField>
                </div>
              </div>

              <FormField label={`Assigned parks (${form.park_ids.length}/4) · ${availableParks.length} active`} full>
                <div className="flex flex-wrap gap-2">
                  {availableParks.length === 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
                      No active parks configured.
                    </div>
                  )}
                  {availableParks.map((p) => {
                    const sel = form.park_ids.includes(p.id);
                    const others = sharedWithByParkId[p.id] || [];
                    const shared = others.length > 0;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={()=>togglePark(p.id)}
                        title={shared ? `Also assigned to: ${others.map(o => `${o.full_name} (@${o.username})`).join(", ")}` : undefined}
                        className={[
                          "rounded-full border px-3 py-1 text-xs inline-flex items-center gap-1.5 transition-colors",
                          sel ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary",
                          shared && !sel ? "border-amber-300 bg-amber-50 text-amber-900" : "",
                        ].join(" ")}
                        data-testid={`sup-park-toggle-${p.code}`}
                      >
                        <span>{p.code} — {p.name}</span>
                        {shared && (
                          <span
                            className={[
                              "ml-1 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                              sel ? "bg-white/20 text-white" : "bg-amber-100 text-amber-900 border border-amber-300",
                            ].join(" ")}
                            data-testid={`sup-park-shared-badge-${p.code}`}
                          >
                            Shared · {others.length}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  ITCOT (PMC) supervisors may share parks — only one visits at a time. Parks already assigned to
                  another supervisor are marked <span className="text-amber-700 font-medium">Shared</span>; you will
                  be asked to confirm before saving an overlap.
                </div>
              </FormField>
              <FormField label="Active" full>
                <Switch checked={form.active} onCheckedChange={(v)=>setForm({...form,active:v})} />
              </FormField>

              <FormField label="Hourly self-attendance cadence (override)" full>
                <div className="flex items-center gap-3 flex-wrap">
                  <select
                    className="border rounded-md px-3 py-2 text-sm bg-background"
                    value={form.hourly_cadence_minutes ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setForm({ ...form, hourly_cadence_minutes: raw === "" ? null : Number(raw) });
                    }}
                    data-testid="sup-hourly-cadence-select"
                  >
                    <option value="">Use park / global default</option>
                    <option value="15">Every 15 minutes</option>
                    <option value="30">Every 30 minutes</option>
                    <option value="60">Every 60 minutes (1 hour)</option>
                    <option value="90">Every 90 minutes</option>
                    <option value="120">Every 2 hours</option>
                    <option value="180">Every 3 hours</option>
                  </select>
                  <div className="text-[11px] text-slate-500">
                    Overrides the park-level cadence for this supervisor's hourly self-attendance check-in.
                  </div>
                </div>
              </FormField>

              <FormField label="Allow manual gallery upload" full>
                <div className="flex items-start gap-3">
                  <Switch
                    checked={!!form.allow_gallery_upload}
                    onCheckedChange={(v)=>setForm({...form, allow_gallery_upload: !!v})}
                    data-testid="sup-allow-gallery-toggle"
                  />
                  <div className="text-[11px] text-slate-500 max-w-md">
                    When enabled, this supervisor can upload photos from the device gallery for attendance
                    capture even when the park is not marked Not Visited. Useful for sites with poor network
                    where live-camera capture is unreliable.
                  </div>
                </div>
              </FormField>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={() => submit(false)} disabled={saving} data-testid="sup-save-button">{saving ? "Saving…" : "Save"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    }>
      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <>
          {/* KPI strip */}
          <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="sup-summary-strip">
            <Stat label="Total supervisors" value={users.length} icon={Users} accent="amber" />
            <Stat label="Active" value={users.filter(u=>u.active).length} icon={Users} accent="amber" />
            <Stat label="Inactive" value={users.filter(u=>!u.active).length} icon={Users} accent="slate" />
            <Stat label="Active parks" value={activeParks.length} icon={TreePine} accent="emerald" />
            <Stat
              label="Parks without supervisor"
              value={orphanParks.length}
              icon={AlertTriangle}
              accent={orphanParks.length > 0 ? "rose" : "emerald"}
              testid="sup-orphan-parks"
            />
          </div>

          {/* Orphan parks callout */}
          {orphanParks.length > 0 && (
            <Card className="mb-3 border-rose-200 bg-rose-50/40">
              <CardContent className="flex flex-wrap items-center gap-2 p-3">
                <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
                <span className="text-xs font-semibold uppercase tracking-wider text-rose-900">
                  {orphanParks.length} park{orphanParks.length === 1 ? "" : "s"} without ITCOT supervisor
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {orphanParks.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800 ring-1 ring-rose-200" data-testid={`sup-orphan-chip-${p.code}`}>
                      <span className="font-mono font-semibold">{p.code}</span>
                      <span>{p.name}</span>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, username, email or phone…"
                value={search}
                onChange={(e)=>setSearch(e.target.value)}
                className="pl-8"
                data-testid="sup-search-input"
              />
            </div>
            <div className="text-xs text-muted-foreground">{list.length} of {users.length} supervisors</div>
          </div>

          {users.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No PMC supervisors yet.</CardContent></Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sup-table">
                  <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                    <tr className="border-b border-slate-200">
                      <Th>#</Th>
                      <Th className="text-left">Full name</Th>
                      <Th>Username</Th>
                      <Th className="text-left">Email</Th>
                      <Th className="text-left">Phone</Th>
                      <Th className="text-left">Assigned parks</Th>
                      <Th>Docs</Th>
                      <Th>Status</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length === 0 ? (
                      <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-muted-foreground">No supervisors match your search.</td></tr>
                    ) : list.map((u, idx) => {
                      const rowCls = !u.active ? "bg-slate-100 text-slate-500 border-l-2 border-l-slate-400" : "hover:bg-amber-50/50";
                      return (
                        <tr key={u.id} className={`border-b border-slate-100 ${rowCls}`} data-testid={`sup-row-${u.username}`}>
                          <Td><span className="font-mono text-xs text-slate-500 tabular-nums">{idx + 1}</span></Td>
                          <Td className="text-left">
                            <button onClick={()=>openView(u)} className="text-left font-medium text-slate-900 hover:text-amber-700">{u.full_name}</button>
                          </Td>
                          <Td><span className="font-mono text-xs text-slate-700">@{u.username}</span></Td>
                          <Td className="text-left text-xs text-slate-600">{u.email || "—"}</Td>
                          <Td className="text-left text-xs text-slate-600">{u.phone || "—"}</Td>
                          <Td className="text-left">
                            <div className="flex flex-wrap items-center gap-1">
                              {(u.park_ids || []).length === 0
                                ? <span className="text-xs text-slate-400 italic">none</span>
                                : (u.park_ids || []).map((pid) => parksMap[pid] ? (
                                    <span key={pid} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800 ring-1 ring-emerald-200">
                                      <span className="font-mono font-semibold">{parksMap[pid].code}</span>
                                      <span className="max-w-[110px] truncate">{parksMap[pid].name}</span>
                                    </span>
                                  ) : null)}
                              {u.park_ids?.length === 4 && (
                                <span className="rounded bg-amber-200 text-amber-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">rotation</span>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <div className="flex justify-center gap-1">
                              <span title="ID card" className={`grid h-5 w-5 place-items-center rounded ${u.has_id_card_attachment ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-300"}`}>
                                <Shield className="h-3 w-3" />
                              </span>
                              <span title="Bank passbook" className={`grid h-5 w-5 place-items-center rounded ${u.has_bank_passbook_attachment ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-300"}`}>
                                <Wallet className="h-3 w-3" />
                              </span>
                            </div>
                          </Td>
                          <Td>
                            {u.active
                              ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900">Active</span>
                              : <span className="inline-flex items-center gap-1 rounded-full bg-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">Inactive</span>}
                          </Td>
                          <Td>
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-sky-700 hover:bg-sky-50" onClick={()=>openView(u)} data-testid={`sup-view-${u.username}`}><Eye className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={()=>openEdit(u)} data-testid={`sup-edit-${u.username}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={()=>remove(u)} data-testid={`sup-delete-${u.username}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
                <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-emerald-700" />Park chip = code · name supervisor patrols</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-amber-200 ring-1 ring-amber-400" />4 parks = on rotation</span>
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-slate-200 ring-1 ring-slate-400" />Inactive supervisor</span>
                <span className="ml-auto">Click name or eye icon for full details</span>
              </div>
            </Card>
          )}
        </>
      )}

      {/* VIEW dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sup-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewUser?.full_name}</DialogTitle>
            <div className="text-xs text-muted-foreground">
              <span className="font-mono">@{viewUser?.username}</span>
              {" · ID card #"}{viewUser?.id_card_number || "—"}
              {" · "}
              {viewUser?.active ? <span className="text-emerald-700 font-semibold">Active</span> : <span className="text-slate-500 font-semibold">Inactive</span>}
            </div>
          </DialogHeader>

          {viewUser && (
            <div className="grid gap-3">
              {/* Contact */}
              <Section title="Contact" icon={Phone}>
                <KV label="Phone" value={viewUser.phone} />
                <KV label="Email" value={viewUser.email} icon={Mail} />
              </Section>

              {/* Parents */}
              <Section title="Parents / Next of kin" icon={Users} accent="amber">
                <KV label="Parent name" value={viewUser.parent_name} />
                <KV label="Parent phone" value={viewUser.parent_phone} />
              </Section>

              {/* Emergency */}
              <Section title="Emergency contact" icon={AlertTriangle} accent="rose">
                <KV label="Name" value={viewUser.emergency_contact_name} />
                <KV label="Phone" value={viewUser.emergency_contact_phone} />
              </Section>

              {/* Identity */}
              <Section title="Identity document" icon={Shield} accent="sky">
                <KV label="Type" value={viewUser.id_card_type ? (ID_TYPES.find((t)=>t.v===viewUser.id_card_type)?.label || viewUser.id_card_type) : "—"} />
                <KV label="Number" value={viewUser.id_card_number} />
                {viewUser.id_card_type === "other" && <KV label="Specified" value={viewUser.id_card_other_label} />}
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  {viewUser.has_id_card_attachment ? (
                    <Button variant="outline" size="sm" onClick={()=>downloadAttachment(viewUser.id, "id_card", viewUser.username)} data-testid="sup-view-download-idcard">
                      <FileText className="mr-1 h-3.5 w-3.5" /> Download ID card
                    </Button>
                  ) : (
                    <span className="text-[11px] text-slate-500">No file on record</span>
                  )}
                </div>
              </Section>

              {/* Bank */}
              <Section title="Bank account" icon={Wallet} accent="emerald">
                <KV label="Account #" value={viewUser.bank_account_number} />
                <KV label="IFSC" value={viewUser.bank_ifsc} />
                <KV label="Bank" value={viewUser.bank_name} />
                <KV label="Branch" value={viewUser.bank_branch} />
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  {viewUser.has_bank_passbook_attachment ? (
                    <Button variant="outline" size="sm" onClick={()=>downloadAttachment(viewUser.id, "bank_passbook", viewUser.username)} data-testid="sup-view-download-passbook">
                      <FileText className="mr-1 h-3.5 w-3.5" /> Download passbook
                    </Button>
                  ) : (
                    <span className="text-[11px] text-slate-500">No file on record</span>
                  )}
                </div>
              </Section>

              {/* Assignment */}
              <Section title="Assigned parks" icon={MapPin} accent="amber">
                <div className="col-span-2 flex flex-wrap gap-1.5">
                  {(viewUser.park_ids || []).length === 0
                    ? <span className="text-xs text-slate-500 italic">No parks assigned</span>
                    : (viewUser.park_ids || []).map((pid) => parksMap[pid] ? (
                        <span key={pid} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800 ring-1 ring-emerald-200">
                          <span className="font-mono font-semibold">{parksMap[pid].code}</span>
                          <span>{parksMap[pid].name}</span>
                        </span>
                      ) : null)}
                  {viewUser.park_ids?.length === 4 && (
                    <span className="rounded bg-amber-200 text-amber-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">rotation</span>
                  )}
                </div>
              </Section>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={()=>setViewOpen(false)}>Close</Button>
            {viewUser && <Button onClick={()=>{ setViewOpen(false); openEdit(viewUser); }} data-testid="sup-view-edit"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Park overlap confirmation dialog */}
      <Dialog open={!!overlapConfirm} onOpenChange={(v) => { if (!v) setOverlapConfirm(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="sup-overlap-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-10">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Park already assigned
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p className="text-slate-700">
              The following park(s) are already assigned to other ITCOT (PMC) supervisors.
              Multiple supervisors may share a park, but only one will visit at a time.
              Do you want to proceed?
            </p>
            <ul className="divide-y rounded-md border bg-amber-50/40 text-xs">
              {(overlapConfirm?.items || []).map((it, i) => (
                <li key={i} className="px-3 py-2" data-testid={`sup-overlap-row-${i}`}>
                  <div className="font-semibold text-slate-900">
                    {it.park?.code ? `${it.park.code} — ` : ""}{it.park?.name || "—"}
                  </div>
                  <div className="mt-0.5 text-slate-600">
                    Already assigned to:{" "}
                    <span className="font-medium text-amber-900">
                      {it.sups.map((s) => `${s.full_name} (@${s.username})`).join(", ")}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverlapConfirm(null)} data-testid="sup-overlap-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => submit(true)}
              disabled={saving}
              data-testid="sup-overlap-confirm-btn"
            >
              {saving ? "Saving…" : "Yes, assign anyway"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function Th({ children, className = "" }) {
  return <th className={`whitespace-nowrap px-3 py-2 text-center text-[11px] font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`whitespace-nowrap px-3 py-2 text-center align-middle ${className}`}>{children}</td>;
}
function Stat({ label, value, icon: Icon, accent = "amber", testid }) {
  const map = {
    amber: { ring: "border-amber-200 bg-amber-50/60", icon: "text-amber-700" },
    slate: { ring: "border-slate-200 bg-slate-50/60", icon: "text-slate-500" },
    emerald: { ring: "border-emerald-200 bg-emerald-50/60", icon: "text-emerald-700" },
    rose: { ring: "border-rose-300 bg-rose-50/80", icon: "text-rose-700" },
  }[accent] || { ring: "border-amber-200 bg-amber-50/60", icon: "text-amber-700" };
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 ${map.ring}`} data-testid={testid}>
      <Icon className={`h-4 w-4 shrink-0 ${map.icon}`} />
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-600">{label}</div>
        <div className="font-mono text-base font-semibold tabular-nums text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <div className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

const SECTION_ACCENTS = {
  default: "border-slate-200 bg-slate-50/40",
  amber: "border-amber-200 bg-amber-50/60",
  rose: "border-rose-200 bg-rose-50/60",
  sky: "border-sky-200 bg-sky-50/60",
  emerald: "border-emerald-200 bg-emerald-50/60",
};
function Section({ title, icon: Icon, accent = "default", children }) {
  const a = SECTION_ACCENTS[accent] || SECTION_ACCENTS.default;
  return (
    <div className={`rounded-md border p-3 ${a}`}>
      <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {title}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">{children}</div>
    </div>
  );
}
function KV({ label, value, icon: Icon }) {
  return (
    <div className="text-xs">
      <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="flex items-center gap-1 font-medium text-slate-900">
        {Icon ? <Icon className="h-3 w-3 text-slate-400" /> : null}
        {value ? <span>{value}</span> : <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}
